import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Loader2, Trophy, Ban, Pencil, FilePlus2, Repeat, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime, timeAgo, fmtMoney } from "@/lib/format";
import AuctionFields, { DEFAULT_AUCTION_CFG, AUCTION_TYPE_LABELS, auctionPayload } from "@/components/AuctionFields";

const AUCTION_STATUS = {
  scheduled: "bg-slate-100 text-slate-600 border-slate-200",
  live: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  extended: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  award_pending: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
  awarded: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  no_winner: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  cancelled: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};
const money = (v, cur = "INR") => (v == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(v).toLocaleString("en-IN")}`);

const SELLER_STATUS_LABEL = { invited: "Eligible", opened: "Eligible", intends: "Eligible", participated: "Quotation Submitted", declined: "Declined", removed: "Removed" };

function toScopeOptions(d) {
  const sellers = (d.invites || [])
    .filter((i) => i.supplier_company_id)
    .map((i) => ({
      id: i.supplier_company_id, name: i.supplier_name,
      statusLabel: SELLER_STATUS_LABEL[i.status] || "Eligible",
      selectable: !["declined", "removed"].includes(i.status),
    }));
  const items = ((d.enquiry && d.enquiry.items) || [])
    .filter((it) => it.status !== "removed")
    .map((it, n) => ({ id: it.id || it.material_code, line: n + 1, material_code: it.material_code,
                       description: it.description, quantity: it.quantity, uom: it.uom }));
  return { sellers, items };
}

function ConfigSummary({ cfg, scopeOptions, testid }) {
  if (!scopeOptions) return null;
  const nSellers = Array.isArray(cfg.supplier_company_ids) ? cfg.supplier_company_ids.length : scopeOptions.sellers.filter((s) => s.selectable).length;
  const nItems = Array.isArray(cfg.item_ids) ? cfg.item_ids.length : scopeOptions.items.length;
  const itemCodes = (Array.isArray(cfg.item_ids) ? scopeOptions.items.filter((i) => cfg.item_ids.includes(i.id)) : scopeOptions.items).map((i) => `${i.material_code}`).join(", ");
  return (
    <div className="mt-3 rounded-md border bg-slate-50 px-3 py-2 text-xs" data-testid={testid}>
      <p><span className="font-semibold uppercase tracking-wide">Auction Summary — </span>{AUCTION_TYPE_LABELS[cfg.auction_type]} · {nSellers} Seller{nSellers === 1 ? "" : "s"} · {nItems} Item{nItems === 1 ? "" : "s"}</p>
      {itemCodes && <p className="mt-0.5 text-muted-foreground">Items being auctioned: {itemCodes}</p>}
    </div>
  );
}

function Countdown({ endAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const s = Math.max(0, Math.floor((new Date(endAt).getTime() - now) / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0"), m = String(Math.floor((s % 3600) / 60)).padStart(2, "0"), sec = String(s % 60).padStart(2, "0");
  return <span className="font-mono font-semibold" data-testid="auction-countdown">{s === 0 ? "Auction Closed" : `${h}:${m}:${sec} remaining`}</span>;
}

function ServerTimer({ label, targetAt, serverTime, testid }) {
  const [now, setNow] = useState(Date.now());
  const sync = useRef(null);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { if (serverTime) sync.current = { s: new Date(serverTime).getTime(), l: Date.now() }; }, [serverTime]);
  const base = sync.current ? sync.current.s + (now - sync.current.l) : now;
  const s = Math.max(0, Math.floor((new Date(targetAt).getTime() - base) / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0"), m = String(Math.floor((s % 3600) / 60)).padStart(2, "0"), sec = String(s % 60).padStart(2, "0");
  return (
    <span className="flex items-center gap-2" data-testid={testid}>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="font-mono text-sm font-bold tabular-nums" data-testid={`${testid}-value`}>{h}:{m}:{sec}</span>
    </span>
  );
}

export default function AuctionPanel({ enquiryId }) {
  const [state, setState] = useState(null);
  const [missing, setMissing] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState(DEFAULT_AUCTION_CFG);
  const [saving, setSaving] = useState(false);
  const [awardTarget, setAwardTarget] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [reauctionOpen, setReauctionOpen] = useState(false);
  const [reauctionReason, setReauctionReason] = useState("");
  const [reauctionCfg, setReauctionCfg] = useState(DEFAULT_AUCTION_CFG);
  const [scopeOptions, setScopeOptions] = useState(null);
  const [showScope, setShowScope] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get(`/enquiries/${enquiryId}/auction`)
      .then((r) => { setState(r.data); setMissing(false); })
      .catch((e) => { if (e?.response?.status === 404) setMissing(true); });
    api.get(`/enquiries/${enquiryId}/auction/history`).then((r) => setRounds(r.data.rounds || [])).catch(() => {});
    api.get(`/enquiries/${enquiryId}`).then((r) => setScopeOptions(toScopeOptions(r.data))).catch(() => {});
  }, [enquiryId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (missing) {
    return (
      <div className="rounded-lg border bg-card" data-testid="auction-panel-empty">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
          <div>
            <h2 className="font-heading text-base font-semibold">Auction</h2>
            <p className="text-sm text-muted-foreground">No auction configured. Optionally add a reverse e-auction stage to this event.</p>
          </div>
          <Button data-testid="enable-auction-btn" onClick={() => { setCfg(DEFAULT_AUCTION_CFG); setCfgOpen(true); }} className="bg-slate-900 hover:bg-slate-800">
            <Gavel className="mr-1.5 h-4 w-4" /> Enable Auction
          </Button>
        </div>
        <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl" data-testid="auction-config-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">Configure Auction</DialogTitle>
              <DialogDescription>Rules lock once the auction starts. Sellers never see competitor identities.</DialogDescription>
            </DialogHeader>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setSaving(true);
              try {
                await api.post(`/enquiries/${enquiryId}/auction`, auctionPayload(cfg));
                toast.success("Auction scheduled — sellers notified (email simulated)");
                setCfgOpen(false);
                load();
              } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
            }}>
              <AuctionFields cfg={cfg} onChange={setCfg} sellerOptions={scopeOptions?.sellers} itemOptions={scopeOptions?.items} />
              <ConfigSummary cfg={cfg} scopeOptions={scopeOptions} testid="auction-config-summary" />
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setCfgOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="auction-config-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm & Schedule Auction
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  if (!state) return null;

  const { auction: a, participants, bids, actions, stats } = state;
  const terminal = ["closed", "cancelled", "no_winner", "award_pending", "awarded"].includes(a.status);
  const awardable = a.status === "award_pending" || (a.status === "closed" && !!a.winner_supplier_id);
  const winner = participants.find((p) => p.supplier_company_id === (a.awarded_supplier_id || a.winner_supplier_id));

  const award = async () => {
    setSaving(true);
    try {
      await api.post(`/enquiries/${enquiryId}/auction/award`, { supplier_company_id: awardTarget });
      toast.success("Seller awarded — notifications sent");
      setAwardTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border bg-card" data-testid="auction-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Gavel className="h-4 w-4 text-slate-500" />
          <h2 className="font-heading text-base font-semibold" data-testid="auction-name">{a.name}</h2>
          <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{AUCTION_TYPE_LABELS[a.auction_type]}</span>
          <span data-testid="auction-status" className={`rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${AUCTION_STATUS[a.status] || AUCTION_STATUS.closed}`}>{a.status.replace(/_/g, " ")}</span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 font-mono text-xs font-medium text-slate-600" data-testid="auction-round-badge">Round {state.round_number || a.round_number || 1}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {!terminal && a.status === "scheduled" && (
            <ServerTimer label="Auction Starts In" targetAt={a.start_at} serverTime={state.server_time} testid="auction-starts-in" />
          )}
          {!terminal && a.status !== "scheduled" && a.auction_type === "japanese" && (
            <ServerTimer label="Response Time Remaining" targetAt={state.current_round_ends_at || a.end_at} serverTime={state.server_time} testid="round-time-remaining" />
          )}
          {!terminal && a.status !== "scheduled" && a.auction_type !== "japanese" && <Countdown endAt={a.end_at} />}
          {terminal && (
            <Button size="sm" data-testid="conduct-reauction-btn" onClick={() => {
              setReauctionReason("");
              setReauctionCfg({ ...DEFAULT_AUCTION_CFG, auction_type: a.auction_type });
              setReauctionOpen(true);
            }} className="bg-slate-900 hover:bg-slate-800">
              <Repeat className="mr-1.5 h-4 w-4" /> Conduct Re-Auction
            </Button>
          )}
          {a.status === "scheduled" && (
            <>
              <Button variant="outline" size="sm" data-testid="modify-auction-btn" onClick={() => {
                setCfg({ ...DEFAULT_AUCTION_CFG, ...a, start_at: a.start_at.slice(0, 16), end_at: a.end_at.slice(0, 16),
                  minimum_price: a.minimum_price ?? "", maximum_price: a.maximum_price ?? "", bid_decrement: a.bid_decrement ?? "",
                  bid_increment: a.bid_increment ?? "", round_interval_seconds: a.round_interval_seconds ?? "",
                  extension_trigger_seconds: a.extension_trigger_seconds ?? "", extension_duration_seconds: a.extension_duration_seconds ?? "",
                  supplier_company_ids: participants.map((p) => p.supplier_company_id), item_ids: a.item_ids || null });
                setCfgOpen(true);
              }}><Pencil className="mr-1 h-3.5 w-3.5" /> Modify Auction</Button>
              <Button variant="outline" size="sm" data-testid="cancel-auction-btn" className="border-red-200 text-red-700 hover:bg-red-50"
                onClick={async () => { try { await api.post(`/enquiries/${enquiryId}/auction/cancel`); toast.success("Auction cancelled"); load(); } catch (e) { toast.error(formatApiError(e)); } }}>
                <Ban className="mr-1 h-3.5 w-3.5" /> Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 px-5 py-4 md:grid-cols-4" data-testid="auction-stats">
        <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Starting Price</p><p className="mt-1 font-mono text-sm font-semibold">{money(a.starting_price, a.currency)}</p></div>
        <div><p className="text-xs uppercase tracking-wide text-muted-foreground">{a.auction_type === "english" ? "Best Bid" : "Current / Final Price"}</p><p className="mt-1 font-mono text-sm font-semibold" data-testid="auction-best">{money(stats.final_price ?? stats.best_bid ?? stats.current_price, a.currency)}</p></div>
        {a.auction_type === "japanese" ? (
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Active / Dropped</p><p className="mt-1 font-mono text-sm" data-testid="japanese-active-dropped">{stats.active_participants} / {stats.dropped_participants ?? 0}</p></div>
        ) : (
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Active / Participants</p><p className="mt-1 font-mono text-sm">{stats.active_participants} / {participants.length}</p></div>
        )}
        {a.auction_type === "japanese" && (
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Joined / Missed</p><p className="mt-1 font-mono text-sm" data-testid="japanese-joined-missed">{stats.joined ?? 0} / {stats.missed ?? 0}</p></div>
        )}
        <div><p className="text-xs uppercase tracking-wide text-muted-foreground">{a.auction_type === "japanese" ? "Current Round" : "Total Bids"}</p><p className="mt-1 font-mono text-sm">{a.auction_type === "japanese" ? stats.current_round : stats.total_bids}</p></div>
        <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Start</p><p className="mt-1 text-xs">{fmtDateTime(a.start_at)} <span className="text-muted-foreground">({a.timezone})</span></p></div>
        <div><p className="text-xs uppercase tracking-wide text-muted-foreground">End</p><p className="mt-1 text-xs">{fmtDateTime(a.end_at)}</p></div>
        {stats.savings != null && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Savings</p><p className="mt-1 font-mono text-sm font-semibold text-[#166534]" data-testid="auction-savings">{money(stats.savings, a.currency)} ({stats.savings_pct}%)</p></div>}
        {stats.avg_bid != null && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Average Bid</p><p className="mt-1 font-mono text-sm">{money(stats.avg_bid, a.currency)}</p></div>}
      </div>

      <div className="border-t px-5 py-3" data-testid="auction-scope">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auction Scope</span>
          <span className="text-xs" data-testid="scope-seller-count">Sellers: {participants.length} Selected</span>
          <span className="text-xs" data-testid="scope-item-count">Items: {(state.scope_items || []).length} Selected</span>
          {a.scope_affected && <span className="rounded-full border border-[#FDE68A] bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold uppercase text-[#92400E]" data-testid="scope-affected-badge">Auction Scope Affected</span>}
          <Button variant="outline" size="sm" data-testid="view-selected-sellers-btn" onClick={() => setShowScope((s) => (s === "sellers" ? null : "sellers"))}>View Selected Sellers</Button>
          <Button variant="outline" size="sm" data-testid="view-selected-items-btn" onClick={() => setShowScope((s) => (s === "items" ? null : "items"))}>View Selected Items</Button>
        </div>
        {showScope === "sellers" && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="selected-sellers-list">
            {participants.map((p) => <span key={p.id} className="rounded-md border bg-slate-50 px-2 py-1 text-xs font-medium">{p.supplier_name}</span>)}
          </div>
        )}
        {showScope === "items" && (
          <div className="mt-2 space-y-1" data-testid="selected-items-list">
            {(state.scope_items || []).map((it) => (
              <div key={it.event_line_item_id || it.material_code} className="flex flex-wrap items-baseline gap-x-3 text-xs" data-testid={`scope-item-${it.material_code}`}>
                <span className="font-mono font-semibold">Line {it.line_no} — {it.material_code}</span>
                <span className="text-muted-foreground">{it.description}</span>
                <span className="font-mono">{it.quantity} {it.uom}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {a.auction_type === "japanese" && terminal && (
        <div className="border-t px-5 py-4" data-testid="japanese-result">
          {a.winner_supplier_id ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#166534]">Auction Closed — Winner Identified</p>
              <div className="mt-2 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Winner</p><p className="mt-1 font-medium" data-testid="japanese-winner-name">{participants.find((p) => p.supplier_company_id === a.winner_supplier_id)?.supplier_name || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Winning Price (Last Accepted)</p><p className="mt-1 font-mono font-semibold" data-testid="japanese-winning-price">{money(a.winner_price ?? a.final_price, a.currency)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Drop-Out Price</p><p className="mt-1 font-mono" data-testid="japanese-dropout-price">{money(a.winner_drop_out_price, a.currency)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Drop Type</p><p className="mt-1" data-testid="japanese-drop-type">{a.winner_drop_out_type === "MANUAL" ? "Manual Drop" : "Automatic Drop"}</p></div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="japanese-winner-reason">
                {a.winner_drop_out_type === "MANUAL"
                  ? `Seller manually dropped out at the ${money(a.winner_drop_out_price, a.currency)} price level.`
                  : `No response at the ${money(a.winner_drop_out_price, a.currency)} price level.`}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="japanese-no-winner">Auction Closed — No Winner (no seller exited with a valid accepted price).</p>
          )}
        </div>
      )}

      <div className="border-t px-5 py-3">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {a.auction_type === "japanese" ? "Seller Joining Status" : "Participants"}
        </h3>
        {a.auction_type === "japanese" ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="japanese-joining-status-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Seller</th>
                <th className="py-2 pr-4 font-medium">Joining Status</th>
                <th className="py-2 pr-4 font-medium">Joined At</th>
                <th className="py-2 pr-4 font-medium">Auction Status</th>
                <th className="py-2 pr-4 font-medium">Dropped</th>
                <th className="py-2 pr-4 font-medium">Last Activity</th>
                {a.status === "award_pending" && <th className="py-2 font-medium text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => {
                const lastAction = actions.find((x) => x.supplier_company_id === p.supplier_company_id);
                const js = p.joining_status || (p.status === "active" ? "joined" : "invited");
                const slug = p.supplier_name.replace(/\W+/g, "-").toLowerCase();
                return (
                  <tr key={p.id} data-testid={`joining-${slug}`} className="border-b last:border-0">
                    <td className="py-2.5 pr-4 font-medium">{p.supplier_name}{(a.awarded_supplier_id === p.supplier_company_id) && <Trophy className="ml-1.5 inline h-3.5 w-3.5 text-amber-500" />}</td>
                    <td className="py-2.5 pr-4">
                      <span data-testid={`joining-status-${slug}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${js === "joined" ? "border-[#BBF7D0] bg-[#DCFCE7] text-[#166534]" : js === "missed" ? "border-[#FECACA] bg-[#FEE2E2] text-[#991B1B]" : "border-slate-200 bg-slate-100 text-slate-600"}`}>
                        {js === "joined" ? "Joined" : js === "missed" ? "Missed / Not Joined" : "Invited"}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-xs">{p.joined_at ? fmtDateTime(p.joined_at) : "—"}</td>
                    <td className="py-2.5 pr-4 text-xs capitalize">{(p.final_status || p.status).replace(/_/g, " ")}</td>
                    <td className="py-2.5 pr-4 text-xs">{p.dropped_round ? `Round ${p.dropped_round}${p.drop_reason === "no_response" ? " (no response)" : ""}` : "—"}</td>
                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">{lastAction ? timeAgo(lastAction.created_at) : "—"}</td>
                    {awardable && (
                      <td className="py-2.5 text-right">
                        <Button size="sm" data-testid={`award-${slug}`} onClick={() => setAwardTarget(p.supplier_company_id)} className="bg-[#166534] hover:bg-[#14532D]">Award</Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="auction-participants-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Seller</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Rank</th>
                <th className="py-2 pr-4 font-medium text-right">Last Bid</th>
                <th className="py-2 pr-4 font-medium text-right">Base Value</th>
                <th className="py-2 pr-4 font-medium text-right">GST</th>
                <th className="py-2 pr-4 font-medium text-right">Final Value</th>
                <th className="py-2 pr-4 font-medium">Last Activity</th>
                {a.status === "award_pending" && <th className="py-2 font-medium text-right">Action</th>}              </tr>
            </thead>
            <tbody>
              {participants.map((p) => {
                const lastAction = actions.find((x) => x.supplier_company_id === p.supplier_company_id) || bids.find((x) => x.supplier_company_id === p.supplier_company_id);
                return (
                  <tr key={p.id} data-testid={`participant-${p.supplier_name.replace(/\W+/g, "-").toLowerCase()}`} className="border-b last:border-0">
                    <td className="py-2.5 pr-4 font-medium">{p.supplier_name}{(a.awarded_supplier_id === p.supplier_company_id) && <Trophy className="ml-1.5 inline h-3.5 w-3.5 text-amber-500" />}</td>
                    <td className="py-2.5 pr-4 text-xs capitalize">{p.final_status || p.status}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs">{p.rank ? `#${p.rank}` : "—"}</td>
                    <td className="py-2.5 pr-4 text-right font-mono text-xs">{money(p.last_bid, a.currency)}</td>
                    <td className="py-2.5 pr-4 text-right font-mono text-xs">{fmtMoney(p.value_breakdown?.base_value)}</td>
                    <td className="py-2.5 pr-4 text-right font-mono text-xs">{p.value_breakdown ? fmtMoney(p.value_breakdown.gst_amount) : "—"}</td>
                    <td className="py-2.5 pr-4 text-right font-mono text-xs font-semibold">{fmtMoney(p.value_breakdown?.final_value)}</td>
                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">{lastAction ? timeAgo(lastAction.created_at) : "—"}</td>
                    {awardable && (
                      <td className="py-2.5 text-right">
                        <Button size="sm" data-testid={`award-${p.supplier_name.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setAwardTarget(p.supplier_company_id)} className="bg-[#166534] hover:bg-[#14532D]">Award</Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        {awardable && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" data-testid="continue-evaluation-btn"
              onClick={async () => { try { await api.post(`/enquiries/${enquiryId}/auction/evaluate`); toast.success("Moved to continued evaluation"); load(); } catch (e) { toast.error(formatApiError(e)); } }}>
              Continue Evaluation (no award)
            </Button>
          </div>
        )}
        {a.status === "awarded" && winner && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-[#BBF7D0] bg-[#DCFCE7]/30 px-4 py-3" data-testid="award-banner">
            <Trophy className="h-4 w-4 text-amber-500" />
            <p className="text-sm font-medium">Awarded to {winner.supplier_name}{a.final_price ? ` at ${money(a.final_price, a.currency)}` : ""}</p>
            {a.po_id
              ? <Button size="sm" variant="outline" data-testid="view-po-btn" onClick={() => navigate(`/buyer/pos/${a.po_id}`)}>View PO</Button>
              : <Button size="sm" data-testid="create-po-btn" className="bg-slate-900 hover:bg-slate-800"
                  onClick={async () => { try { const r = await api.post(`/enquiries/${enquiryId}/auction/create-po`); toast.success(`PO ${r.data.po_number} created`); navigate(`/buyer/pos/${r.data.id}`); } catch (e) { toast.error(formatApiError(e)); } }}>
                  <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> Create Purchase Order
                </Button>}
          </div>
        )}
      </div>

      {bids.length > 0 && (
        <div className="border-t px-5 py-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bid / Action History</h3>
          <div className="max-h-56 space-y-1 overflow-y-auto" data-testid="auction-history">
            {bids.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm" data-testid={`bid-${b.id.slice(0, 8)}`}>
                <span>{b.supplier_name} <span className="text-xs text-muted-foreground">{b.bid_type === "accept" ? "accepted" : "bid"}</span></span>
                <span className="font-mono text-xs font-semibold">{money(b.bid_amount, a.currency)}</span>
                <span className="text-xs text-muted-foreground">{timeAgo(b.created_at)}</span>
              </div>
            ))}
            {actions.filter((x) => ["STAY_IN", "DROP_OUT", "JOINED"].includes(x.action_type)).slice(0, 40).map((x) => {
              const p = participants.find((pp) => pp.supplier_company_id === x.supplier_company_id);
              return (
                <div key={x.id} className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="text-xs">{p?.supplier_name || "Seller"} — {x.action_type.replace(/_/g, " ").toLowerCase()}{x.metadata?.round ? ` (round ${x.metadata.round})` : ""}</span>
                  <span className="text-xs">{timeAgo(x.created_at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {rounds.length > 0 && (
        <div className="border-t px-5 py-3">
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><History className="h-3.5 w-3.5" /> Auction History</h3>
          <div className="space-y-1.5" data-testid="auction-rounds-history">
            {rounds.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm" data-testid={`auction-round-${r.round_number}`}>
                <span className="font-mono text-xs font-semibold">Round {r.round_number}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${AUCTION_STATUS[r.status] || AUCTION_STATUS.closed}`}>{r.status.replace(/_/g, " ")}</span>
                <span className="text-xs text-muted-foreground">{AUCTION_TYPE_LABELS[r.auction_type]}</span>
                {r.winner && <span className="text-xs">Winner: <span className="font-medium">{r.winner}</span></span>}
                {r.final_price != null && <span className="font-mono text-xs font-semibold">Final: {money(r.final_price, r.currency)}</span>}
                {r.reauction_reason && <span className="text-xs text-muted-foreground">Reason: "{r.reauction_reason}"</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {a.auction_type === "japanese" && (state.round_history || []).length > 0 && (
        <div className="border-t px-5 py-3">
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><History className="h-3.5 w-3.5" /> Round History</h3>
          <div className="space-y-1.5" data-testid="japanese-round-history">
            {state.round_history.map((r) => (
              <div key={r.round} className="rounded-md border px-3 py-2 text-sm" data-testid={`japanese-round-${r.round}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-xs font-semibold">Round {r.round}</span>
                  <span className="font-mono text-xs font-semibold" data-testid={`japanese-round-${r.round}-price`}>{money(r.price, a.currency)}</span>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(r.started_at)} → {fmtDateTime(r.ended_at)}</span>
                  <span className="text-xs font-medium text-[#166534]">Stayed: {r.stayed.length}</span>
                  <span className="text-xs font-medium text-[#991B1B]">Dropped: {r.dropped.length}</span>
                </div>
                {r.entries ? (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs" data-testid={`japanese-round-${r.round}-entries`}>
                      <thead>
                        <tr className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                          <th className="py-1 pr-4 font-medium">Seller</th>
                          <th className="py-1 pr-4 font-medium">Action</th>
                          <th className="py-1 font-medium">Accepted Price?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.entries.map((e) => (
                          <tr key={`${e.seller}-${e.action}`} className="border-b last:border-0">
                            <td className="py-1 pr-4">{e.seller}</td>
                            <td className="py-1 pr-4">{e.action.replace(/_/g, " ")}</td>
                            <td className="py-1">{e.accepted ? "Yes" : "No"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {r.stayed.length > 0 && <span className="mr-3">Stayed in: {r.stayed.join(", ")}</span>}
                    {r.dropped.length > 0 && <span>Dropped out: {r.dropped.join(", ")}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={reauctionOpen} onOpenChange={setReauctionOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl" data-testid="reauction-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Start a new auction round?</DialogTitle>
            <DialogDescription>The previous auction results will be retained. Selected sellers will participate in a new auction round (Round {(state.round_number || 1) + 1}).</DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!reauctionReason.trim()) return toast.error("A reason for the re-auction is required");
            const sellerIds = Array.isArray(reauctionCfg.supplier_company_ids)
              ? reauctionCfg.supplier_company_ids
              : (scopeOptions?.sellers || []).filter((s) => s.selectable).map((s) => s.id);
            const itemIds = Array.isArray(reauctionCfg.item_ids) ? reauctionCfg.item_ids : (scopeOptions?.items || []).map((i) => i.id);
            if (!sellerIds.length) return toast.error("Select at least one eligible seller");
            if (!itemIds.length) return toast.error("Please select at least one Event Item for this auction.");
            setSaving(true);
            try {
              await api.post(`/enquiries/${enquiryId}/auction/reauction`, {
                reason: reauctionReason, supplier_company_ids: sellerIds, item_ids: itemIds, config: auctionPayload(reauctionCfg),
              });
              toast.success("Re-auction scheduled — selected sellers notified");
              setReauctionOpen(false);
              load();
            } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
          }} className="space-y-4">
            <div>
              <Label>Reason for Re-Auction *</Label>
              <Textarea data-testid="reauction-reason" required rows={2} className="mt-1.5" placeholder="e.g. Price discovery needs improvement." value={reauctionReason} onChange={(e) => setReauctionReason(e.target.value)} />
            </div>
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" data-testid="use-previous-sellers-btn"
                onClick={() => setReauctionCfg((c) => ({ ...c, supplier_company_ids: participants.map((p) => p.supplier_company_id), item_ids: a.item_ids || c.item_ids }))}>
                Use Previous Seller Selection
              </Button>
            </div>
            <AuctionFields cfg={reauctionCfg} onChange={setReauctionCfg} sellerOptions={scopeOptions?.sellers} itemOptions={scopeOptions?.items} />
            <ConfigSummary cfg={reauctionCfg} scopeOptions={scopeOptions} testid="reauction-config-summary" />
            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" data-testid="reauction-cancel" onClick={() => setReauctionOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="reauction-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Start Re-Auction
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl" data-testid="auction-modify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modify Auction</DialogTitle>
            <DialogDescription>Allowed only before the auction starts. Changes are recorded in the audit log.</DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            try {
              await api.put(`/enquiries/${enquiryId}/auction`, auctionPayload(cfg));
              toast.success("Auction updated");
              setCfgOpen(false);
              load();
            } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
          }}>
            <AuctionFields cfg={cfg} onChange={setCfg} sellerOptions={scopeOptions?.sellers} itemOptions={scopeOptions?.items} />
            <ConfigSummary cfg={cfg} scopeOptions={scopeOptions} testid="auction-modify-summary" />
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setCfgOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="auction-modify-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!awardTarget} onOpenChange={() => setAwardTarget(null)}>
        <DialogContent className="bg-white" data-testid="award-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Award Seller</DialogTitle>
            <DialogDescription>Explicitly award this event to {participants.find((p) => p.supplier_company_id === awardTarget)?.supplier_name}. All participants will be notified of the result.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAwardTarget(null)}>Cancel</Button>
            <Button data-testid="award-confirm-submit" onClick={award} disabled={saving} className="bg-[#166534] hover:bg-[#14532D]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm Award
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
